import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiSearch, FiChevronDown, FiRefreshCw, FiCheck } from 'react-icons/fi';
import '../../styles/QuoteProposal.css';
import '../commissionscan/CommissionScan.css';
import { useToast } from '../../context/ToastContext';

const getToken    = () => localStorage.getItem('toolboxToken') || '';

const POLICY_TYPES = [
  { code: 'GL',    label: 'General Liability' },
  { code: 'WC',    label: 'Workers Compensation' },
  { code: 'BOP',   label: 'Business Owners Policy' },
  { code: 'CYBER', label: 'Cyber Policy' },
];

const US_STATES: Array<{ code: string; label: string }> = [
  { code: 'AL', label: 'Alabama' },         { code: 'AK', label: 'Alaska' },
  { code: 'AZ', label: 'Arizona' },         { code: 'AR', label: 'Arkansas' },
  { code: 'CA', label: 'California' },      { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' },     { code: 'DC', label: 'District of Columbia' },
  { code: 'DE', label: 'Delaware' },        { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' },         { code: 'HI', label: 'Hawaii' },
  { code: 'ID', label: 'Idaho' },           { code: 'IL', label: 'Illinois' },
  { code: 'IN', label: 'Indiana' },         { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' },          { code: 'KY', label: 'Kentucky' },
  { code: 'LA', label: 'Louisiana' },       { code: 'ME', label: 'Maine' },
  { code: 'MD', label: 'Maryland' },        { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' },        { code: 'MN', label: 'Minnesota' },
  { code: 'MS', label: 'Mississippi' },     { code: 'MO', label: 'Missouri' },
  { code: 'MT', label: 'Montana' },         { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' },          { code: 'NH', label: 'New Hampshire' },
  { code: 'NJ', label: 'New Jersey' },      { code: 'NM', label: 'New Mexico' },
  { code: 'NY', label: 'New York' },        { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' },    { code: 'OH', label: 'Ohio' },
  { code: 'OK', label: 'Oklahoma' },        { code: 'OR', label: 'Oregon' },
  { code: 'PA', label: 'Pennsylvania' },    { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' },  { code: 'SD', label: 'South Dakota' },
  { code: 'TN', label: 'Tennessee' },       { code: 'TX', label: 'Texas' },
  { code: 'UT', label: 'Utah' },            { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' },        { code: 'WA', label: 'Washington' },
  { code: 'WV', label: 'West Virginia' },   { code: 'WI', label: 'Wisconsin' },
  { code: 'WY', label: 'Wyoming' },
];

interface CarrierItem {
  carrierName: string;
  carrierLogoURL: string;
}

interface NaicsOption {
  naicsCode: string;
  description: string;
}

interface RateRiskAppetiteSearchProps {
  onGoToHistory: () => void;
}

const RateRiskAppetiteSearch: React.FC<RateRiskAppetiteSearchProps> = () => {
  /* ── Form fields ── */
  const [policyType, setPolicyType]           = useState('');
  const [policyTypeError, setPolicyTypeError] = useState('');
  const [stateCode, setStateCode]             = useState('');
  const [stateError, setStateError]           = useState('');
  const [naicsCode, setNaicsCode]             = useState('');
  const [naicsLabel, setNaicsLabel]           = useState('');
  const [naicsError, setNaicsError]           = useState('');
  const [naicsSearch, setNaicsSearch]         = useState('');
  const [naicsOptions, setNaicsOptions]       = useState<NaicsOption[]>([]);
  const [naicsLoading, setNaicsLoading]       = useState(false);

  /* ── Dropdown open/close ── */
  const [policyOpen, setPolicyOpen] = useState(false);
  const [stateOpen,  setStateOpen]  = useState(false);
  const policyRef = useRef<HTMLDivElement>(null);
  const stateRef  = useRef<HTMLDivElement>(null);

  /* ── Results ── */
  const [loading, setLoading]             = useState(false);
  const [carriers, setCarriers]           = useState<CarrierItem[]>([]);
  const [hasResult, setHasResult]         = useState(false);
  const [successStatus, setSuccessStatus] = useState(false);

  /* ── Toast ── */
  const { showError } = useToast();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Close dropdowns on outside click ── */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (policyRef.current && !policyRef.current.contains(e.target as Node)) setPolicyOpen(false);
      if (stateRef.current  && !stateRef.current.contains(e.target as Node))  setStateOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── NAICS search debounce ── */
  useEffect(() => {
    if (!naicsSearch.trim()) { setNaicsOptions([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setNaicsLoading(true);
    debounceRef.current = setTimeout(() => fetchNaics(naicsSearch), 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [naicsSearch]);

  const fetchNaics = async (text: string) => {
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/RateAppetite/external/naics?searchtext=${encodeURIComponent(text)}`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setNaicsOptions(res.data?.codeInfo || []);
    } catch {
      showError('Unknown error while getting naics code list');
    } finally {
      setNaicsLoading(false);
    }
  };

  const validate = useCallback((): boolean => {
    let valid = true;
    if (!policyType) { setPolicyTypeError('Please select policy type'); valid = false; } else setPolicyTypeError('');
    if (!stateCode)  { setStateError('Please select state');            valid = false; } else setStateError('');
    if (!naicsCode)  { setNaicsError('Please select naics code');       valid = false; } else setNaicsError('');
    return valid;
  }, [policyType, stateCode, naicsCode]);

  const onSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    setHasResult(false);
    setCarriers([]);
    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/RateAppetite/external/appetite`,
        { policyType, state: stateCode, naicsCode },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const data = res.data;
      setSuccessStatus(data.success);
      const list: CarrierItem[] = data.providerInfo?.primaryPreferrefProvider || [];
      setCarriers(list);
      setHasResult(true);
      if (!data.success) {
        showError(data.errorMessage || 'Unknown error while getting rate risk appetite response');
      }
      insertTransaction(data);
    } catch {
      showError('Unknown error while getting rate risk appetite response');
    } finally {
      setLoading(false);
    }
  };

  const insertTransaction = async (appetiteResponse: unknown) => {
    const policyLabel = POLICY_TYPES.find(p => p.code === policyType)?.label || policyType;
    const stateLabel  = US_STATES.find(s => s.code === stateCode)?.label     || stateCode;
    try {
      await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/RateAppetite/availableappetite`,
        {
          policyType: policyLabel,
          state: stateLabel,
          naicsCode: naicsLabel || naicsCode,
          appetiteResponse: JSON.stringify(appetiteResponse),
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
    } catch {
      showError('Unknown error while inserting rate risk appetite response');
    }
  };

  const onTryAgain = () => {
    setPolicyType(''); setPolicyTypeError('');
    setStateCode('');  setStateError('');
    setNaicsCode('');  setNaicsLabel(''); setNaicsSearch(''); setNaicsError('');
    setCarriers([]);   setHasResult(false); setSuccessStatus(false);
  };

  const onNaicsSelect = (option: NaicsOption) => {
    setNaicsCode(option.naicsCode);
    setNaicsLabel(`${option.naicsCode} - ${option.description}`);
    setNaicsSearch('');
    setNaicsOptions([]);
    setNaicsError('');
  };

  const selectedPolicyLabel = POLICY_TYPES.find(p => p.code === policyType)?.label || '';
  const selectedStateLabel  = US_STATES.find(s => s.code === stateCode)?.label     || '';

  const fieldLabelStyle: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: 'var(--text-primary)', marginBottom: 6,
  };
  const requiredMark = <span style={{ color: 'var(--accent-red, #ef4444)' }}> *</span>;

  /* ── Render ── */
  return (
    <>
      <motion.div
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {/* Header */}
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
            Rate Risk Appetite
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-tertiary)' }}>
            Query carrier appetite by policy type, state, and NAICS code.
          </p>
        </div>

        {/* Form row */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>

            {/* ── Policy Type dropdown ── */}
            <div style={{ flex: 1, minWidth: 180, position: 'relative' }} ref={policyRef}>
              <label style={fieldLabelStyle}>Policy Type{requiredMark}</label>
              <div
                className="cs-client-search__input-wrap"
                style={{ borderColor: policyTypeError ? 'var(--accent-red, #ef4444)' : undefined, cursor: 'pointer' }}
                onClick={() => { setPolicyOpen(o => !o); setStateOpen(false); }}
              >
                <span
                  className="cs-client-search__input"
                  style={{
                    display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none',
                    color: selectedPolicyLabel ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  }}
                >
                  {selectedPolicyLabel || 'Select Policy Type'}
                </span>
                <FiChevronDown
                  size={14}
                  style={{
                    color: 'var(--text-tertiary)', flexShrink: 0, marginRight: 10,
                    transform: policyOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 150ms',
                  }}
                />
              </div>
              {policyTypeError && (
                <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>
                  {policyTypeError}
                </span>
              )}
              {policyOpen && (
                <ul className="cs-client-dropdown" style={{ marginTop: 2 }}>
                  {POLICY_TYPES.map(p => (
                    <li
                      key={p.code}
                      className="cs-client-dropdown__item"
                      onClick={() => {
                        setPolicyType(p.code);
                        setPolicyTypeError('');
                        setPolicyOpen(false);
                      }}
                    >
                      <span className="cs-client-dropdown__name" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {p.label}
                        {policyType === p.code && <FiCheck size={13} style={{ color: 'var(--accent-purple-light)', flexShrink: 0 }} />}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ── State dropdown ── */}
            <div style={{ flex: 1, minWidth: 180, position: 'relative' }} ref={stateRef}>
              <label style={fieldLabelStyle}>State{requiredMark}</label>
              <div
                className="cs-client-search__input-wrap"
                style={{ borderColor: stateError ? 'var(--accent-red, #ef4444)' : undefined, cursor: 'pointer' }}
                onClick={() => { setStateOpen(o => !o); setPolicyOpen(false); }}
              >
                <span
                  className="cs-client-search__input"
                  style={{
                    display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none',
                    color: selectedStateLabel ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  }}
                >
                  {selectedStateLabel || 'Select State'}
                </span>
                <FiChevronDown
                  size={14}
                  style={{
                    color: 'var(--text-tertiary)', flexShrink: 0, marginRight: 10,
                    transform: stateOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 150ms',
                  }}
                />
              </div>
              {stateError && (
                <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>
                  {stateError}
                </span>
              )}
              {stateOpen && (
                <ul className="cs-client-dropdown" style={{ marginTop: 2, maxHeight: 220, overflowY: 'auto' }}>
                  {US_STATES.map(s => (
                    <li
                      key={s.code}
                      className="cs-client-dropdown__item"
                      onClick={() => {
                        setStateCode(s.code);
                        setStateError('');
                        setStateOpen(false);
                      }}
                    >
                      <span className="cs-client-dropdown__name" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {s.label}
                        {stateCode === s.code && <FiCheck size={13} style={{ color: 'var(--accent-purple-light)', flexShrink: 0 }} />}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ── NAICS Code autocomplete ── */}
            <div style={{ flex: 2, minWidth: 220, position: 'relative' }}>
              <label style={fieldLabelStyle}>NAICS Code{requiredMark}</label>
              <div
                className="cs-client-search__input-wrap"
                style={{ borderColor: naicsError ? 'var(--accent-red, #ef4444)' : undefined }}
              >
                <FiSearch size={15} className="cs-client-search__icon" />
                <input
                  className="cs-client-search__input"
                  type="text"
                  placeholder={naicsLabel || 'Search and select the NAICS code'}
                  value={naicsSearch}
                  maxLength={255}
                  onChange={e => {
                    setNaicsSearch(e.target.value);
                    if (naicsCode) { setNaicsCode(''); setNaicsLabel(''); }
                    if (naicsError) setNaicsError('');
                  }}
                />
                {naicsLoading && <span className="cs-client-search__spinner" />}
              </div>
              {naicsError && (
                <span style={{ fontSize: 11, color: 'var(--accent-red, #ef4444)', marginTop: 3, display: 'block' }}>
                  {naicsError}
                </span>
              )}
              {/* NAICS results dropdown */}
              {naicsSearch.trim() && !naicsLoading && (
                <ul className="cs-client-dropdown" style={{ marginTop: 2 }}>
                  {naicsOptions.length === 0 ? (
                    <li className="cs-client-dropdown__item" style={{ cursor: 'default', color: 'var(--text-secondary)', opacity: 0.8 }}>
                      No naics code found
                    </li>
                  ) : naicsOptions.map(opt => (
                    <li
                      key={opt.naicsCode}
                      className="cs-client-dropdown__item"
                      onClick={() => onNaicsSelect(opt)}
                    >
                      <span className="cs-client-dropdown__name">{opt.naicsCode} – {opt.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* ── Submit ── */}
            <div style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
              <label style={{ ...fieldLabelStyle, visibility: 'hidden' }}>&nbsp;</label>
              <button
                className="cs-browse-btn cs-browse-btn--sm"
                onClick={onSubmit}
                disabled={loading}
                style={{ marginTop: 0, height: 42, justifyContent: 'center' }}
              >
                {loading
                  ? <><FiRefreshCw size={13} className="csh-spin" /> Checking…</>
                  : <><FiSearch size={13} /> Get Risk Appetite</>}
              </button>
            </div>
          </div>
        </div>

        {/* ── Results ── */}
        {hasResult && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22 }}
          >
            {successStatus && carriers.length > 0 ? (
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 12px' }}>
                  Available Risk Appetite
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                  {carriers.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                        borderRadius: 10, padding: '10px 16px',
                        display: 'flex', alignItems: 'center', gap: 10, minWidth: 160,
                      }}
                    >
                      {c.carrierLogoURL && (
                        <img
                          src={c.carrierLogoURL}
                          alt={c.carrierName}
                          style={{ height: 32, maxWidth: 80, objectFit: 'contain' }}
                          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                        {c.carrierName}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : successStatus && carriers.length === 0 ? (
              <div style={{ padding: '20px 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
                No Risk Appetite is available for the selected criteria.
              </div>
            ) : null}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                className="cs-browse-btn cs-browse-btn--sm"
                onClick={onTryAgain}
              >
                Try Again
              </button>
             
                <button
                  className="cs-browse-btn cs-browse-btn--sm"
                  onClick={() => window.open((process.env.REACT_APP_Rate_UI_Url    || ''), '_blank')}
                  
                >
                   Start Quoting
                </button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </>
  );
};

export default RateRiskAppetiteSearch;

